// frontend/src/components/event/EventRegistrationForm.js
//
// Registration form for events. Handles 5 scenarios based on format + external_registration_url:
//
// 1. online                             → Internal registration (online only)
// 2. in_person + external_registration_url  → Link to external organizer (no internal registration)
// 3. in_person (no external URL)            → Internal registration (in-person only)
// 4. hybrid + external_registration_url     → Online = internal register, Vor Ort = external link
// 5. hybrid (no external URL)               → Two-option selector (online / in-person), both internal
//
// The external_registration_url is independent of pricing_type. It is used when
// in-person ("Vor Ort") attendance is managed by an external organizer.
//
import React, { useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Monitor, MapPin, Loader2, ExternalLink } from "lucide-react";

const EventRegistrationForm = ({
  event,
  onRegister,
  loading = false,
  isUrgent = false,
}) => {
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [participationType, setParticipationType] = useState("online");

  const isInvited =
    event.participant?.source === "invitation" &&
    event.participant?.status === "pending";

  const hasExternalRegistration = !!event.external_registration_url;

  const buttonLabel = isInvited
    ? t({ de: "Einladung annehmen & Zugang erhalten", en: "Accept Invitation & Get Access" })
    : isUrgent
    ? t({ de: "Anmelden & Beitreten", en: "Register & Join" })
    : t({ de: "Jetzt anmelden", en: "Register Now" });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      navigate(`/login?returnUrl=/event/${event.slug}`);
      return;
    }

    onRegister(participationType);
  };

  // Check available spots
  const onlineSpotsAvailable =
    event.capacity?.spotsRemaining?.online === null ||
    event.capacity?.spotsRemaining?.online > 0;
  const inPersonSpotsAvailable =
    event.capacity?.spotsRemaining?.in_person === null ||
    event.capacity?.spotsRemaining?.in_person > 0;

  // For online-only events - external registration doesn't apply
  if (event.format === "online") {
    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        {!onlineSpotsAvailable ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
            {t({ de: "Keine Plätze mehr verfügbar", en: "No spots available" })}
          </div>
        ) : (
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#EE005F] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#cc0050] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-5 h-5 animate-spin" />}
            {buttonLabel}
          </button>
        )}
      </form>
    );
  }

  // For in-person only events with external registration
  if (event.format === "in_person" && hasExternalRegistration) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          {t({
            de: "Die Anmeldung für dieses Event wird vom Veranstalter verwaltet.",
            en: "Registration for this event is managed by the organizer.",
          })}
        </p>
        <a
          href={event.external_registration_url}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full bg-[#EE005F] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#cc0050] transition-colors flex items-center justify-center gap-2"
        >
          {t({ de: "Beim Veranstalter anmelden", en: "Register with Organizer" })}
          <ExternalLink className="w-5 h-5" />
        </a>
      </div>
    );
  }

  // For in-person only events (no external registration)
  if (event.format === "in_person") {
    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        {!inPersonSpotsAvailable ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
            {t({ de: "Keine Plätze mehr verfügbar", en: "No spots available" })}
          </div>
        ) : (
          <>
            <input type="hidden" value="in_person" />
            <button
              type="submit"
              disabled={loading}
              onClick={() => setParticipationType("in_person")}
              className="w-full bg-[#EE005F] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#cc0050] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin" />}
              {buttonLabel}
            </button>
          </>
        )}
      </form>
    );
  }

  // For hybrid events with external registration for in-person
  if (hasExternalRegistration) {
    return (
      <div className="space-y-4">
        <div className="text-sm font-medium text-gray-700 mb-2">
          {t({ de: "Wie möchtest du teilnehmen?", en: "How would you like to participate?" })}
        </div>

        <div className="space-y-3">
          {/* Online Option - internal registration */}
          <div
            className={`p-3 rounded-lg border-2 transition-all flex items-center gap-3 ${
              !onlineSpotsAvailable ? "opacity-50" : "border-gray-200"
            }`}
          >
            <Monitor className="w-5 h-5 text-gray-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm">
                {t({ de: "Online", en: "Online" })}
              </div>
              {event.capacity?.spotsRemaining?.online !== null && (
                <div className="text-xs text-gray-500">
                  {event.capacity.spotsRemaining.online}{" "}
                  {t({ de: "Plätze", en: "spots" })}
                </div>
              )}
            </div>
            {onlineSpotsAvailable ? (
              <form onSubmit={(e) => { e.preventDefault(); if (!isAuthenticated) { navigate(`/login?returnUrl=/event/${event.slug}`); return; } onRegister("online"); }} className="flex-shrink-0">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-[#EE005F] text-white py-2 px-4 rounded-lg text-sm font-semibold hover:bg-[#cc0050] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {t({ de: "Anmelden", en: "Register" })}
                </button>
              </form>
            ) : (
              <div className="text-xs text-yellow-700 flex-shrink-0">
                {t({ de: "Ausgebucht", en: "Fully booked" })}
              </div>
            )}
          </div>

          {/* In-Person Option - external registration */}
          <div className="p-3 rounded-lg border-2 border-gray-200 flex items-center gap-3">
            <MapPin className="w-5 h-5 text-gray-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm">
                {t({ de: "Vor Ort", en: "In-Person" })}{event.location ? ` (${event.location})` : ""}
              </div>
              <div className="text-xs text-gray-500">
                {t({ de: "Extern", en: "External" })}
              </div>
            </div>
            <a
              href={event.external_registration_url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#EE005F] text-white py-2 px-4 rounded-lg text-sm font-semibold hover:bg-[#cc0050] transition-colors flex items-center gap-1 flex-shrink-0"
            >
              {t({ de: "Anmelden", en: "Register" })}
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  // For hybrid events without external registration - show both options as before
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="text-sm font-medium text-gray-700 mb-2">
        {t({ de: "Wie möchtest du teilnehmen?", en: "How would you like to participate?" })}
      </div>

      <div className="space-y-3">
        {/* Online Option */}
        <button
          type="button"
          onClick={() => setParticipationType("online")}
          disabled={!onlineSpotsAvailable}
          className={`w-full p-3 rounded-lg border-2 transition-all flex items-center gap-3 text-left ${
            participationType === "online"
              ? "border-[#EE005F] bg-[#EE005F]/5"
              : "border-gray-200 hover:border-gray-300"
          } ${!onlineSpotsAvailable ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <Monitor
            className={`w-5 h-5 flex-shrink-0 ${
              participationType === "online" ? "text-[#EE005F]" : "text-gray-500"
            }`}
          />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm">
              {t({ de: "Online", en: "Online" })}
            </div>
            {event.capacity?.spotsRemaining?.online !== null && (
              <div className="text-xs text-gray-500">
                {event.capacity.spotsRemaining.online}{" "}
                {t({ de: "Plätze", en: "spots" })}
              </div>
            )}
          </div>
        </button>

        {/* In-Person Option */}
        <button
          type="button"
          onClick={() => setParticipationType("in_person")}
          disabled={!inPersonSpotsAvailable}
          className={`w-full p-3 rounded-lg border-2 transition-all flex items-center gap-3 text-left ${
            participationType === "in_person"
              ? "border-[#EE005F] bg-[#EE005F]/5"
              : "border-gray-200 hover:border-gray-300"
          } ${!inPersonSpotsAvailable ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <MapPin
            className={`w-5 h-5 flex-shrink-0 ${
              participationType === "in_person"
                ? "text-[#EE005F]"
                : "text-gray-500"
            }`}
          />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm">
              {t({ de: "Vor Ort", en: "In-Person" })}{event.location ? ` (${event.location})` : ""}
            </div>
            {event.capacity?.spotsRemaining?.in_person !== null && (
              <div className="text-xs text-gray-500">
                {event.capacity.spotsRemaining.in_person}{" "}
                {t({ de: "Plätze", en: "spots" })}
              </div>
            )}
          </div>
        </button>
      </div>

      <button
        type="submit"
        disabled={
          loading ||
          (participationType === "online" && !onlineSpotsAvailable) ||
          (participationType === "in_person" && !inPersonSpotsAvailable)
        }
        className="w-full bg-[#EE005F] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#cc0050] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {loading && <Loader2 className="w-5 h-5 animate-spin" />}
        {buttonLabel}
      </button>
    </form>
  );
};

export default EventRegistrationForm;
